import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import { parseRuleForCertIds, ruleHasExpiryCheck, ruleHasIsActiveCheck } from './AccessRuleBuilder';

export default function AccessRuleCard({
  rule,
  teamLabel,
  editingRuleId,
  getCertNameById,
  onEdit,
  onDelete,
  loading,
}) {
  const [expanded, setExpanded] = useState(false);

  const certIds = parseRuleForCertIds(rule.ruleCode);
  const hasExpiry = ruleHasExpiryCheck(rule.ruleCode);
  const hasIsActive = ruleHasIsActiveCheck(rule.ruleCode);
  const isEditing = editingRuleId === rule.id;

  return (
    <Card
      variant="outlined"
      sx={{ borderColor: isEditing ? 'primary.main' : undefined }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {rule.name}
            </Typography>
            {rule.description && (
              <Typography variant="body2" color="text.secondary">
                {rule.description}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {teamLabel && (
              <Chip label={teamLabel} size="small" variant="outlined" color="secondary" />
            )}
            {isEditing && <Chip label="Editing" color="primary" size="small" />}
            {rule.isEnabled === false && (
              <Chip label="Disabled" color="warning" size="small" variant="outlined" />
            )}
          </Box>
        </Box>

        {certIds.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
              {hasExpiry && (
                <Chip label="Expiry check" size="small" variant="outlined" color="info" />
              )}
              {hasIsActive && (
                <Chip label="Active check" size="small" variant="outlined" color="info" />
              )}
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                {certIds.length} certificate{certIds.length !== 1 ? 's' : ''} required
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {certIds.map((id) => {
                const certName = getCertNameById(id);
                const isKnown = !certName.startsWith('Unknown');
                return (
                  <Chip
                    key={id}
                    label={certName}
                    size="small"
                    color={isKnown ? 'success' : 'default'}
                    variant={isKnown ? 'filled' : 'outlined'}
                  />
                );
              })}
            </Box>
          </Box>
        )}

        {certIds.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            No certificate IDs detected in rule code
          </Typography>
        )}

        <Box sx={{ mt: 1.5 }}>
          <Button
            size="small"
            variant="text"
            onClick={() => setExpanded(!expanded)}
            sx={{ textTransform: 'none', p: 0 }}
          >
            {expanded ? 'Hide raw code' : 'Show raw code'}
          </Button>
          <Collapse in={expanded}>
            <Box
              sx={{
                mt: 1,
                p: 2,
                bgcolor: 'grey.100',
                borderRadius: 1,
                overflow: 'auto',
              }}
            >
              <Typography variant="body2" fontFamily="monospace" whiteSpace="pre-wrap">
                {rule.ruleCode}
              </Typography>
            </Box>
          </Collapse>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Button onClick={() => onEdit(rule)} color="primary" size="small" disabled={loading}>
            Edit
          </Button>
          <Button onClick={() => onDelete(rule)} color="error" size="small" disabled={loading}>
            Delete
          </Button>
        </Box>
      </Box>
    </Card>
  );
}
